import { useEffect, useRef } from "react";
import { useAuth } from "@workos-inc/authkit-react";
import {
  ATTR_COOKIE_NAME,
  parseAttributionCookie,
  readCookie,
} from "@/shared/lib/attribution";
import { useExtendedAuth } from "@/shared/auth/AuthContext";
import { config } from "@/shared/config";

// Fires once per signed-in session: if the marketing site set a
// pk_attribution cookie on the shared .proposalkit.io domain, PUT the
// payload to the server so it lands on the users row. Server enforces
// first-touch (no overwrite) and re-sanitises, so this is safe to fire
// even if the cookie has been tampered with.
//
// Gated on userTrialData being loaded: that's the signal the users row
// exists (the WorkOS user.created webhook may lag behind the browser's
// post-auth redirect by a second or two). Firing beforehand would race
// against the webhook and PUT against a non-existent row.
export function AttributionStamper() {
  const { getAccessToken } = useAuth();
  const { userTrialData } = useExtendedAuth();
  const fired = useRef(false);

  useEffect(() => {
    if (fired.current) return;
    if (!userTrialData) return;

    const cookie = readCookie(document.cookie, ATTR_COOKIE_NAME);
    if (!cookie) return;
    const payload = parseAttributionCookie(cookie);
    if (!payload) return;

    fired.current = true;

    void (async () => {
      try {
        const token = await getAccessToken();
        await fetch(`${config.apiBaseUrl}/api/me/attribution`, {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        });
      } catch {
        // Attribution is best-effort telemetry — don't block the app or
        // surface errors to the user. A failed stamp just means this
        // visitor stays un-attributed.
      }
    })();
  }, [userTrialData, getAccessToken]);

  return null;
}
